import {
  useBrokersServiceBrokersBrokersChangeAdminCreate,
  useBrokersServiceBrokersBrokersInviteAdminCreate,
  useBrokersServiceBrokersBrokersListKey
} from '@insurance/api/queries'
import { BrokerInvite } from '@insurance/api/requests'
import { BrokerUserSelect } from '@insurance/components'
import { useValidationsField } from '@insurance/hooks'
import { PhoneInput } from '@shared/components'
import { useInvalidateQueries } from '@shared/hooks'
import { replaceEmptyWithUndefined, showApiErrors } from '@shared/utils'
import { Col, Form, Input, message, Modal, Row, Segmented } from 'antd'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  brokerId: number
  open: boolean
  onClose: () => void
}

const EXISTING_USER = 'existing_user'
const NEW_USER = 'new_user'

export const ChangeAdminModal = ({ brokerId, open, onClose }: Props) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const { invalidateQueries } = useInvalidateQueries()
  const { required, fullName, phoneNumber, personalCode } =
    useValidationsField()
  const [mode, setMode] = useState<string>(EXISTING_USER)

  const { mutateAsync: changeAdminMutation, isPending: isChangePending } =
    useBrokersServiceBrokersBrokersChangeAdminCreate({
      onError: (error) => showApiErrors(error, form)
    })

  const { mutateAsync: inviteAdminMutation, isPending: isInvitePending } =
    useBrokersServiceBrokersBrokersInviteAdminCreate({
      onError: (error) => showApiErrors(error, form)
    })

  const isLoading = isChangePending || isInvitePending

  const onFinish = async (values: BrokerInvite & { admin: string }) => {
    if (mode === EXISTING_USER) {
      await changeAdminMutation({
        id: brokerId,
        requestBody: { admin: Number(values.admin) }
      })
    } else {
      const { admin, ...inviteData } = values
      await inviteAdminMutation({
        id: brokerId,
        requestBody: replaceEmptyWithUndefined({
          ...inviteData,
          force: true
        }) as BrokerInvite
      })
    }

    message.success(t('change_admin_success'))
    invalidateQueries([useBrokersServiceBrokersBrokersListKey])
    handleClose()
  }

  const handleClose = () => {
    form.resetFields()
    setMode(EXISTING_USER)
    onClose()
  }

  const handleModeChange = (value: string) => {
    form.resetFields()
    setMode(value)
  }

  return (
    <Modal
      title={t('change_admin')}
      open={open}
      onCancel={handleClose}
      okText={t('save')}
      cancelText={t('cancel')}
      okButtonProps={{
        form: 'change-admin-form',
        htmlType: 'submit',
        loading: isLoading
      }}
      destroyOnHidden
    >
      <Segmented
        style={{ marginBottom: 24 }}
        block
        value={mode}
        onChange={handleModeChange}
        options={[
          { label: t('existing_user'), value: EXISTING_USER },
          { label: t('new_user'), value: NEW_USER }
        ]}
      />

      <Form
        id="change-admin-form"
        form={form}
        layout="vertical"
        onFinish={onFinish}
      >
        {mode === EXISTING_USER ? (
          <Form.Item name="admin" label={t('administrator')} rules={[required]}>
            <BrokerUserSelect
              broker={String(brokerId)}
              excludeStatus={['deleted']}
              placeholder={t('select_administrator')}
            />
          </Form.Item>
        ) : (
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label={t('administrator_last_name')}
                name="last_name"
                rules={[required, fullName]}
              >
                <Input placeholder={t('administrator_last_name')} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={t('administrator_first_name')}
                name="first_name"
                rules={[required, fullName]}
              >
                <Input placeholder={t('administrator_first_name')} />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                label={t('identification_number')}
                name="identification_number"
                normalize={(value, prevValue) =>
                  String(value).length > 13 ? prevValue : value
                }
                rules={[required, personalCode]}
              >
                <Input placeholder={t('identification_number')} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={t('phone')}
                name="phone"
                rules={[required, phoneNumber]}
              >
                <PhoneInput />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={t('email')}
                name="email"
                rules={[{ type: 'email' }, required]}
              >
                <Input placeholder={t('email')} />
              </Form.Item>
            </Col>
          </Row>
        )}
      </Form>
    </Modal>
  )
}
